import { useProductsContextSafe } from '@/components/config-editor/providers';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { useSearchProducts, useProductByVariantId } from '@/lib/api/products';
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";
import { ChevronDown, ImageOff, Package, Search, X } from 'lucide-react';
import { memo, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface VariantSelectorProps {
  value: string;
  onChange: (value: string) => void;
  storeId?: string; // Store ID for server-side search
  // Dependency injection - optional products data
  products?: any[];
  loading?: boolean;
}

/**
 * Memoized variant card component
 */
const VariantCard = memo(
  ({
    variant,
    isSelected,
    onSelect,
    getPriceInfo,
    className,
  }: {
    variant: any;
    isSelected: boolean;
    onSelect: () => void;
    getPriceInfo: (v: any) => { amount: number; currency: string } | null;
    className?: string;
  }) => {
    const priceInfo = getPriceInfo(variant);

    return (
      <label
        className={cn(
          'flex w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-md border p-2 transition-all',
          'hover:border-primary/50 hover:bg-accent',
          isSelected && 'border-primary bg-accent ring-primary/20 ring-1',
        )}
      >
        <RadioGroupItem value={variant.id} className="h-3.5 w-3.5 shrink-0" />

        {variant.imageUrl ? (
          <img src={variant.imageUrl} alt={variant.name} className="h-8 w-8 shrink-0 rounded object-cover" />
        ) : (
          <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded">
            <ImageOff className="text-muted-foreground h-3.5 w-3.5" />
          </div>
        )}

        <div className="min-w-0 flex-1 overflow-hidden">
          <h3 className="truncate text-xs font-medium leading-tight">{variant.name}</h3>
          <div className="flex items-center gap-2">
            {variant.sku && <p className="text-muted-foreground truncate text-[10px]">{variant.sku}</p>}
            <p className="text-primary text-[10px] font-semibold">
              {priceInfo ? formatMoney(priceInfo.amount, priceInfo.currency) : 'N/A'}
            </p>
          </div>
        </div>
      </label>
    );
  },
);

VariantCard.displayName = 'VariantCard';

function VariantSelector({
  value,
  onChange,
  storeId,
  products: injectedProducts,
  loading: injectedLoading,
}: VariantSelectorProps) {
  const instanceId = useId();
  const { t } = useTranslation('storefront');
  // State
  const [openProducts, setOpenProducts] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounced search update
  const updateDebouncedSearch = useDebouncedCallback((query: string) => {
    setDebouncedSearch(query);
  }, 300);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateDebouncedSearch(query);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
  };

  // Get products from context or injected props
  const contextData = useProductsContextSafe();
  const contextProducts = injectedProducts !== undefined ? injectedProducts : contextData?.products || [];
  const contextLoading = injectedLoading !== undefined ? injectedLoading : contextData?.loading || false;

  // Fetch product containing selected variant separately (for initialization)
  const { data: selectedProductData, isLoading: isLoadingSelected } = useProductByVariantId(
    value || '',
    !!value && !!storeId && !debouncedSearch && contextProducts.length === 0,
  );

  // Load all products on mount when storeId is provided (and no context products)
  const { data: allProductsData, isLoading: isLoadingAll } = useSearchProducts(
    {
      storeId: storeId || null,
      productId: null,
      searchValue: null,
    },
    { enabled: !!storeId && !debouncedSearch && contextProducts.length === 0 },
  );

  // Use server-side search only when there's a search query and storeId
  const shouldSearch = !!storeId && !!debouncedSearch && debouncedSearch.trim().length > 0;

  const { data: searchResults, isLoading: isSearching } = useSearchProducts(
    {
      storeId: storeId || null,
      productId: null,
      searchValue: debouncedSearch || null,
    },
    { enabled: shouldSearch },
  );

  // Determine which products to use (priority: search > context > initial load)
  let allProducts: any[] = [];
  if (shouldSearch && searchResults) {
    allProducts = searchResults;
  } else if (contextProducts.length > 0) {
    allProducts = contextProducts;
  } else {
    allProducts = allProductsData || [];
  }
  const loading = shouldSearch ? isSearching : contextLoading || isLoadingSelected || isLoadingAll;

  // Client-side limiting for performance (show max 100 products)
  const DISPLAY_LIMIT = 100;
  const products = useMemo(() => {
    // If there's a selected value, inject product at top if not already in list
    if (value && selectedProductData) {
      const isAlreadyInList = allProducts.some((p) => p.variants?.some((v: any) => v.id === value));

      if (!isAlreadyInList) {
        // Selected variant's product is NOT in the list
        // Inject it at the top, then show others up to limit
        const others = allProducts.slice(0, DISPLAY_LIMIT - 1);
        return [selectedProductData, ...others];
      } else {
        // Selected variant's product IS in the list, move it to top
        const selectedProduct = allProducts.find((p) => p.variants?.some((v: any) => v.id === value));
        const others = allProducts.filter((p) => p.id !== selectedProduct?.id).slice(0, DISPLAY_LIMIT - 1);
        return [selectedProduct, ...others];
      }
    }

    return allProducts.slice(0, DISPLAY_LIMIT);
  }, [allProducts, value, selectedProductData]);

  const hasMoreResults = allProducts.length > DISPLAY_LIMIT;

  // Count total variants for display
  const totalVariants = useMemo(() => {
    return products.reduce((sum: number, product: any) => sum + (product.variants?.length || 0), 0);
  }, [products]);

  // Open the first product by default when products load
  useEffect(() => {
    if (products.length > 0) {
      setOpenProducts(new Set([products[0].id]));
    }
  }, [products.length > 0 && products[0]?.id]);

  const toggleProduct = (productId: string) => {
    setOpenProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  // Expand all products when searching and result count is small
  useEffect(() => {
    if (debouncedSearch && products.length > 0 && products.length <= 10) {
      setOpenProducts(new Set(products.map((p: any) => p.id)));
    }
  }, [debouncedSearch, products]);

  // Auto-expand product containing the selected variant and scroll into view
  useEffect(() => {
    if (value && products.length > 0) {
      for (const product of products) {
        const hasSelectedVariant = product.variants?.some((v: any) => v.id === value);
        if (hasSelectedVariant) {
          setOpenProducts((prev) => new Set([...prev, product.id]));

          // Scroll selected variant into view after a short delay
          setTimeout(() => {
            const selectedElement = document.querySelector(`[data-state="checked"][value="${value}"]`);
            if (selectedElement) {
              selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          break;
        }
      }
    }
  }, [value, products]);

  // Memoized price info getter
  const getPriceInfo = useMemo(
    () =>
      (variant: any): { amount: number; currency: string } | null => {
        if (variant.prices && variant.prices.length > 0) {
          const price = variant.prices[0];
          const currencies = Object.keys(price.currencyOptions || {});
          if (currencies.length > 0) {
            const currency = currencies[0];
            const currencyData = price.currencyOptions[currency];
            if (currencyData && typeof currencyData.amount === 'number') {
              return { amount: currencyData.amount, currency: currency.toUpperCase() };
            }
          }
        }
        return null;
      },
    [],
  );

  // Only show skeletons on INITIAL load (no data yet)
  // This prevents the "accordion effect" when refreshing with existing data
  if (loading && products.length === 0) {
    return (
      <div className="space-y-2">
        <div className="animate-pulse">
          <div className="mb-2 h-9 rounded bg-gray-200" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-md border p-2">
            <div className="flex items-center justify-between">
              <div className="flex-1 space-y-1">
                <div className="h-4 w-48 rounded bg-gray-200" />
                <div className="h-3 w-64 rounded bg-gray-200" />
              </div>
              <div className="h-4 w-4 rounded bg-gray-200" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-2 overflow-hidden">
      {/* Search Input */}
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
        <Input
          type="text"
          placeholder={t('configEditor.formElements.searchProductsAndVariants')}
          value={searchQuery}
          onChange={handleSearchChange}
          className="h-8 pl-8 pr-8 text-xs"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Results count */}
      {debouncedSearch && products.length > 0 && (
        <div className="text-muted-foreground flex items-center gap-1.5 text-[10px]">
          <Package className="h-3 w-3" />
          <span>
            {products.length} products • {totalVariants} variants
          </span>
        </div>
      )}

      {/* More results available notice */}
      {!debouncedSearch && !value && hasMoreResults && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 dark:border-blue-900 dark:bg-blue-950">
          <p className="text-[10px] text-blue-700 dark:text-blue-300">
            <strong>
              Showing {DISPLAY_LIMIT} of {allProducts.length} products.
            </strong>{' '}
            Use search to find more variants.
          </p>
        </div>
      )}

      {/* Product List */}
      <RadioGroup name={`variant-selector-${instanceId}`} value={value} onValueChange={onChange}>
        <div className="h-[400px] space-y-1 overflow-y-auto p-1.5">
          {!products || products.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed p-4">
              <Package className="text-muted-foreground mb-1 h-5 w-5" />
              <p className="text-muted-foreground text-xs">{debouncedSearch ? 'No match' : 'No products'}</p>
            </div>
          ) : (
            products.map((product) => {
              const isOpen = openProducts.has(product.id);
              const variantCount = product.variants?.length || 0;

              // Get first variant image (prefer first variant with image)
              const firstVariantImage =
                product.variants?.find((v: any) => v.imageUrl)?.imageUrl || product.variants?.[0]?.imageUrl;

              return (
                <Collapsible key={product.id} open={isOpen} onOpenChange={() => toggleProduct(product.id)}>
                  <div className="w-full min-w-0 overflow-hidden rounded-md border">
                    <CollapsibleTrigger className="hover:bg-accent flex w-full min-w-0 items-center gap-2 overflow-hidden p-2 transition-colors">
                      {firstVariantImage ? (
                        <img
                          src={firstVariantImage}
                          alt={product.name}
                          className="h-7 w-7 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div className="bg-muted flex h-7 w-7 shrink-0 items-center justify-center rounded">
                          <ImageOff className="text-muted-foreground h-3.5 w-3.5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1 overflow-hidden text-left">
                        <h2 className="truncate text-xs font-medium leading-tight">{product.name}</h2>
                        <span className="text-muted-foreground text-[10px]">
                          {variantCount} {variantCount === 1 ? 'variant' : 'variants'}
                        </span>
                      </div>
                      <ChevronDown
                        className={cn(
                          'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                          isOpen && 'rotate-180',
                        )}
                      />
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="space-y-1 p-2 pt-0">
                        {product.variants && product.variants.length > 0 ? (
                          product.variants.map((variant: any) => (
                            <VariantCard
                              key={variant.id}
                              variant={variant}
                              isSelected={value === variant.id}
                              onSelect={() => onChange(variant.id)}
                              getPriceInfo={getPriceInfo}
                            />
                          ))
                        ) : (
                          <p className="text-muted-foreground text-[10px]">No variants</p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })
          )}
        </div>
      </RadioGroup>
    </div>
  );
}

export default VariantSelector;
